import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Button from '@components/button/Button'
import Icon from '@components/icon/Icons'
import SectionTitle from '@components/sectionTitle/SectionTitle'

import useNavigation from '@layout/useNavigation'
import useReducer from '@store/useReducer'
import useI18n from '@i18n/useI18n'
import * as SiteStore from '@store/site/site'
import useBreakpoint from '@utils/useBreakpoints'

import api from '@api/api'
import logger from '@utils/logger'

import theme from '@theme/Theme'

const MAX_TENANTS_DISPLAYED = 5

type StatMapping = { [tenantId: string]: IncidentsStatistiquesResponse }

const TenantsHomeSection = () => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const tenants = useReducer(SiteStore.store, (s) => s.tenants)
  const site = useReducer(SiteStore.store, (s) => s.site)

  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  const [stats, setStats] = React.useState<StatMapping>({})

  const displayedTenants = React.useMemo(() => tenants.current.slice(0, MAX_TENANTS_DISPLAYED), [])

  React.useEffect(() => {
    if (site) {
      Promise.all(displayedTenants.map((t) => api.incident.stats(site.id, undefined, t.tenant.id)))
        .then((sts) => {
          const mapping = sts.reduce(
            (acc, cur, idx) => ({ ...acc, [displayedTenants[idx].tenant.id]: cur }),
            {} as StatMapping
          )

          setStats(mapping)
        })
        .catch(logger.error)
    }
  }, [displayedTenants, site])

  const onSeeAllTenantsClicked = () => {
    navigation.push('/tenants')
  }

  const seeAllTenantsButton = {
    label: i18n.t('screens.site.allTenants'),
    onClick: onSeeAllTenantsClicked,
  }

  const renderTenant = ({ tenant, dates }: Tenant) => {
    const stat = (stats && stats[tenant.id] && stats[tenant.id].enCours) || 0
    return (
      <View key={tenant.id}>
        <TenantCard
          key={tenant.id}
          activeOpacity={0.9}
          onPress={() => navigation.push(`/tenant/${tenant.id}`, { tenantId: tenant.id })}>
          <TopContainer>
            <TenantInfos>
              <TenantName ellipsizeMode="tail" numberOfLines={1}>
                {tenant.nomLocatairePermanent}
              </TenantName>
              {!isPhone && <TenantDate>{i18n.t('screens.site.tenantDate', { start: dates.start })}</TenantDate>}
            </TenantInfos>
            {isPhone && !!stats && !!stats[tenant.id] && (
              <IncidentIndicator>
                <IncidentIndicatorTotal>{stat}</IncidentIndicatorTotal>
              </IncidentIndicator>
            )}
            <Icon name="arrow" size={24} color={theme.colors.primary} />
          </TopContainer>
          {!isPhone && !!stats && !!stats[tenant.id] && (
            <BottomContainer>
              <IncidentTotal>
                <IncidentIndicator>
                  <IncidentIndicatorTotal>{stat}</IncidentIndicatorTotal>
                </IncidentIndicator>
                <IncidentLabel>{i18n.t('screens.site.incidentsInProgress', { count: stat })}</IncidentLabel>
              </IncidentTotal>
            </BottomContainer>
          )}
        </TenantCard>
        {isPhone && <Separator key={`separator_${tenant.id}`} />}
      </View>
    )
  }

  return (
    <>
      <SectionTitle title={i18n.t('screens.site.tenants')} button={isPhone ? undefined : seeAllTenantsButton} />
      <TenantsContainer>
        {displayedTenants.length > 0 ? (
          displayedTenants.map(renderTenant)
        ) : (
          <MissingInfo>{i18n.t('screens.site.noTenant')}</MissingInfo>
        )}
        {isPhone && (
          <ButtonContainer>
            <Button
              status="active"
              label={i18n.t('screens.site.allTenants')}
              onClick={onSeeAllTenantsClicked}
              color="light"
            />
          </ButtonContainer>
        )}
      </TenantsContainer>
    </>
  )
}

export default TenantsHomeSection
const TenantCard = styled(TouchableOpacity)`
  border-radius: 6px;
  overflow: hidden;
  ${(props) => props.theme.breakpoint !== 'phone' && props.theme.constants.shadow};
  ${(props) =>
    props.theme.breakpoint !== 'phone' &&
    `
    margin: 0px 16px 16px 0px;
    min-width: 360px;
    border-radius: 6px;
    background-color: ${props.theme.colors.background};
    `}
`
const TopContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '10px 16px' : '16px 24px')};
  background-color: ${(props) => props.theme.colors.background};
  flex-direction: row;
`
const TenantsContainer = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? 8 : 16)}px 0;
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
    border-radius: 6px;
    overflow: hidden;
    background-color: ${props.theme.colors.background};
    `
      : `flex-wrap: wrap;`}
  ${(props) => props.theme.breakpoint === 'phone' && props.theme.constants.shadow}
`
const TenantInfos = styled(View)`
  flex: 1;
`
const Separator = styled(View)`
  height: 1px;
  background-color: ${(props) => props.theme.colors.disable};
  margin: 0 0 0 16px;
`
const TenantName = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Body : props.theme.fonts.Headline)};
`
const TenantDate = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.secondaryText};
`
const ButtonContainer = styled(View)`
  padding: 16px;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const BottomContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 8 : 24)}px;
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.background : props.theme.colors.incident};
`

const IncidentTotal = styled(View)`
  border-radius: 6px;
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  align-items: center;
  ${(props) => props.theme.breakpoint === 'phone' && `justify-content: center;`}

  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `text-align: center;`
      : `
      height: 64px;
      padding: 16px;
      background-color: ${props.theme.colors.linkDisabled};
      `};
`

const IncidentIndicator = styled(View)`
  height: ${(props) => (props.theme.breakpoint === 'phone' ? 24 : 32)}px;
  width: ${(props) => (props.theme.breakpoint === 'phone' ? 24 : 32)}px;
  border-radius: 36px;
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.secondaryActive : props.theme.colors.primary};
  align-items: center;
  justify-content: center;
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin-bottom: 4px;` : `margin-right: 16px;`)}
`

const IncidentIndicatorTotal = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Caption1 : props.theme.fonts.Subheadline)};
  text-align: center;
  color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.primaryText : props.theme.colors.background};
  align-items: center;
`

const IncidentLabel = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.secondaryText : props.theme.colors.primary};
  font-size: ${(props) => (props.theme.breakpoint === 'phone' ? 11 : 16)}px;
`
